<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { XMarkIcon, ChevronDownIcon, PlusIcon } from '@heroicons/vue/24/solid';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { playerLogTypeOptions } from '@/utils/playerLogTypeOptions';
import type {
    PlayerLogRange,
    PlayerLogTypeCount,
} from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * Filter rules for the player logs, one per line: field, operator, value.
 *
 * The shape of the main filter builder, with the parts this area doesn't need
 * taken out — no saved views, no favourites, no nested and/or groups. Every
 * rule combines with and, which is what you want when narrowing a log.
 *
 * The operators are the point. In a log you usually want everything EXCEPT the
 * noisy type, so "is none of" does more work than "is one of" — the old chip
 * row could only ever add, never subtract.
 */
const range = defineModel<PlayerLogRange>('range', { required: true });
const messageFilter = defineModel<string>('messageFilter', { required: true });
const messageMode = defineModel<'contains' | 'not_contains'>('messageMode', { required: true });
const trackFilter = defineModel<string>('trackFilter', { required: true });
const types = defineModel<string[]>('types', { required: true });
const typesMode = defineModel<'in' | 'not_in'>('typesMode', { required: true });

const props = withDefaults(
    defineProps<{
        /**
         * Types the server saw while scanning. The counts are not shown — they
         * order the list, so the types you meet most are at the top.
         */
        availableTypes?: PlayerLogTypeCount[];
    }>(),
    { availableTypes: () => [] }
);

type RuleField = 'range' | 'types' | 'message' | 'track';

const FIELD_LABELS: Record<RuleField, string> = {
    range: 'Time range',
    types: 'Log type',
    message: 'Message',
    track: 'Track',
};

const RANGE_OPTIONS: Array<{ value: PlayerLogRange; label: string }> = [
    { value: 'all', label: 'All time' },
    { value: '1h', label: 'Last hour' },
    { value: '24h', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
];

/** Which rules are on screen. A rule with a value is always shown. */
const addedFields = ref<Set<RuleField>>(new Set());

function hasValue(field: RuleField): boolean {
    if (field === 'range') return range.value !== 'all';
    if (field === 'types') return types.value.length > 0;
    if (field === 'message') return messageFilter.value.trim() !== '';
    return trackFilter.value.trim() !== '';
}

const shownFields = computed<RuleField[]>(() =>
    (Object.keys(FIELD_LABELS) as RuleField[]).filter(
        (field) => hasValue(field) || addedFields.value.has(field)
    )
);

const addableFields = computed<RuleField[]>(() =>
    (Object.keys(FIELD_LABELS) as RuleField[]).filter((field) => !shownFields.value.includes(field))
);

const filtersActive = computed(() => (Object.keys(FIELD_LABELS) as RuleField[]).some(hasValue));

function addField(field: RuleField): void {
    addedFields.value = new Set(addedFields.value).add(field);
}

/** Removing a rule clears its value — a rule off screen must not still filter. */
function removeField(field: RuleField): void {
    const next = new Set(addedFields.value);
    next.delete(field);
    addedFields.value = next;
    if (field === 'range') range.value = 'all';
    if (field === 'types') types.value = [];
    if (field === 'message') messageFilter.value = '';
    if (field === 'track') trackFilter.value = '';
}

function clearAll(): void {
    addedFields.value = new Set();
    range.value = 'all';
    types.value = [];
    messageFilter.value = '';
    trackFilter.value = '';
    messageMode.value = 'contains';
    typesMode.value = 'in';
}

function toggleType(type: string, selected: boolean): void {
    const next = new Set(types.value);
    if (selected) {
        next.add(type);
    } else {
        next.delete(type);
    }
    types.value = [...next];
}

const typesLabel = computed(() => {
    if (types.value.length === 0) return 'Choose types…';
    const [first, second, ...rest] = types.value;
    const shown = [first, second].filter(Boolean).join(', ');
    return rest.length > 0 ? `${shown} +${rest.length}` : shown;
});

const rangeLabel = computed(
    () => RANGE_OPTIONS.find((option) => option.value === range.value)?.label ?? 'All time'
);

const typeOptions = computed(() => playerLogTypeOptions(props.availableTypes, types.value));

// Once every rule is gone, forget the "added but empty" ones too, so reopening
// the panel starts clean rather than showing blank rules from last time.
watch(filtersActive, (active) => {
    if (!active && addedFields.value.size > 0 && shownFields.value.every((f) => !hasValue(f))) {
        addedFields.value = new Set();
    }
});
</script>

<template>
    <div class="flex flex-col gap-1" data-testid="lzp-log-filter-rules">
        <div
            v-for="field in shownFields"
            :key="field"
            class="flex items-center gap-1.5"
            :data-testid="`lzp-log-rule-${field}`"
        >
            <span class="text-muted-foreground w-[68px] shrink-0 text-xs">
                {{ FIELD_LABELS[field] }}
            </span>

            <!-- Time range has one operator ("is"), so it shows only its value. -->
            <DropdownMenu v-if="field === 'range'">
                <DropdownMenuTrigger
                    class="border-border/60 bg-muted/40 hover:bg-muted flex min-w-0 flex-1 items-center justify-between gap-1 rounded border px-2 py-1 text-xs"
                    data-testid="lzp-log-rule-range-value"
                >
                    <span class="truncate">{{ rangeLabel }}</span>
                    <ChevronDownIcon class="text-muted-foreground size-3 shrink-0" aria-hidden="true" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem
                        v-for="option in RANGE_OPTIONS"
                        :key="option.value"
                        @select="range = option.value"
                    >
                        {{ option.label }}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <template v-else-if="field === 'types'">
                <DropdownMenu>
                    <DropdownMenuTrigger
                        class="border-border/60 bg-muted/40 hover:bg-muted flex w-[92px] shrink-0 items-center justify-between gap-1 whitespace-nowrap rounded border px-2 py-1 text-xs"
                        data-testid="lzp-log-rule-types-operator"
                    >
                        <span>{{ typesMode === 'in' ? 'is one of' : 'is none of' }}</span>
                        <ChevronDownIcon class="text-muted-foreground size-3 shrink-0" aria-hidden="true" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem @select="typesMode = 'in'">is one of</DropdownMenuItem>
                        <DropdownMenuItem @select="typesMode = 'not_in'">is none of</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                    <DropdownMenuTrigger
                        class="border-border/60 bg-muted/40 hover:bg-muted flex min-w-0 flex-1 items-center justify-between gap-1 rounded border px-2 py-1 text-xs"
                        data-testid="lzp-log-rule-types-value"
                    >
                        <span class="truncate">{{ typesLabel }}</span>
                        <ChevronDownIcon class="text-muted-foreground size-3 shrink-0" aria-hidden="true" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" class="max-h-64 w-56 overflow-auto">
                        <p
                            v-if="typeOptions.length === 0"
                            class="text-muted-foreground px-2 py-1.5 text-xs"
                        >
                            No types in the entries scanned.
                        </p>
                        <DropdownMenuCheckboxItem
                            v-for="option in typeOptions"
                            :key="option.type"
                            :model-value="types.includes(option.type)"
                            :data-testid="`lzp-log-type-option-${option.type}`"
                            @update:model-value="toggleType(option.type, $event)"
                            @select.prevent
                        >
                            <span class="min-w-0 flex-1 truncate font-mono">{{ option.type }}</span>
                        </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </template>

            <template v-else-if="field === 'message'">
                <DropdownMenu>
                    <DropdownMenuTrigger
                        class="border-border/60 bg-muted/40 hover:bg-muted flex w-[92px] shrink-0 items-center justify-between gap-1 whitespace-nowrap rounded border px-2 py-1 text-xs"
                        data-testid="lzp-log-rule-message-operator"
                    >
                        <span class="truncate">
                            {{ messageMode === 'contains' ? 'contains' : "doesn't" }}
                        </span>
                        <ChevronDownIcon class="text-muted-foreground size-3 shrink-0" aria-hidden="true" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem @select="messageMode = 'contains'">contains</DropdownMenuItem>
                        <DropdownMenuItem @select="messageMode = 'not_contains'">
                            does not contain
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Input
                    v-model="messageFilter"
                    placeholder="goodcopy"
                    class="h-[26px] min-w-0 flex-1 text-xs"
                    data-testid="lzp-log-rule-message-value"
                />
            </template>

            <template v-else>
                <span class="text-muted-foreground w-[92px] shrink-0 whitespace-nowrap text-xs">name or code</span>
                <Input
                    v-model="trackFilter"
                    placeholder="DIWE5043"
                    class="h-[26px] min-w-0 flex-1 text-xs"
                    data-testid="lzp-log-rule-track-value"
                />
            </template>

            <button
                type="button"
                class="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
                :aria-label="`Remove ${FIELD_LABELS[field]} filter`"
                :data-testid="`lzp-log-rule-remove-${field}`"
                @click="removeField(field)"
            >
                <XMarkIcon class="size-3.5" aria-hidden="true" />
            </button>
        </div>

        <div class="flex items-center gap-2">
            <DropdownMenu v-if="addableFields.length > 0">
                <DropdownMenuTrigger
                    class="border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 flex items-center gap-1 rounded border border-dashed px-2 py-1 text-xs"
                    data-testid="lzp-log-add-filter"
                >
                    <PlusIcon class="size-3" aria-hidden="true" />
                    Add filter
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem
                        v-for="field in addableFields"
                        :key="field"
                        @select="addField(field)"
                    >
                        {{ FIELD_LABELS[field] }}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <button
                v-if="filtersActive"
                type="button"
                class="text-muted-foreground hover:text-foreground text-xs"
                data-testid="lzp-log-clear-filters"
                @click="clearAll"
            >
                Clear all
            </button>
        </div>
    </div>
</template>
